/**
 * App-experience contract: the single source of types, limits, validators and
 * TTS identity for the admin-controlled intro video and Abby welcome.
 *
 * Dependency-free plain TypeScript. Backend, admin (browser) and mobile
 * (Hermes) all import this file, so it must not use zod, node:crypto or
 * Buffer. SHA-256 is pure JS (see ./sha256).
 */
import { createSha256, sha256, type Sha256Hasher } from './sha256';

export { createSha256, sha256, type Sha256Hasher };

// ---- Limits (defined ONCE, here) -------------------------------------------
export const VIDEO_UPLOAD_MAX_BYTES = 2 * 1024 ** 3;
export const VIDEO_OUTPUT_MAX_BYTES = 20 * 1024 * 1024;
export const VIDEO_MAX_DURATION_MS = 60_000;
export const VIDEO_MIN_ASPECT = 0.5; // width / height
export const VIDEO_MAX_ASPECT = 2.0; // width / height
export const VIDEO_OUTPUT_WIDTH = 1080;
export const VIDEO_RECOMMENDED_MIN_SHORT_SIDE = 720;
export const VIDEO_MAX_FPS = 60;
export const VIDEO_UPLOAD_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm', 'mkv'] as const;
export const DESCRIPTION_MAX_CHARS = 1000;
export const WELCOME_MIN_PARAGRAPHS = 1;
export const WELCOME_MAX_PARAGRAPHS = 12;
export const WELCOME_PARAGRAPH_MAX_CHARS = 400;
export const PARAGRAPH_GAP_MS = 300;

// ---- TTS identity (the asset generator imports these) ----------------------
export const TTS_MODEL = 'gpt-4o-mini-tts-2025-12-15';
export const TTS_VOICE = 'marin';
export const TTS_INSTRUCTIONS =
  'Warm, unhurried, intimate. Slight pause between sentences. Conversational tone, not announcer-y.';

// ---- Types ------------------------------------------------------------------
export type ExperienceSurface = 'intro_video' | 'abby_welcome';
export type ExperienceLocale = 'en' | 'es';

export interface VideoAsset {
  url: string;
  poster_url: string;
  sha256: string;
  bytes: number;
  duration_ms: number;
  width: number;
  height: number;
  description: string | null;
}

export interface WelcomeTiming {
  totalDurationMs: number;
  paragraphs: { index: number; startMs: number; endMs: number }[];
}

export interface WelcomeVoiceLocale {
  paragraphs: string[];
  script_hash: string;
  audio_url: string;
  audio_sha256: string;
  timing: WelcomeTiming;
}

export interface IntroVideoConfig {
  schema: 1;
  enabled: boolean;
  audience_pct: number;
  videos: Record<ExperienceLocale, VideoAsset | null>;
}

export interface AbbyWelcomeConfig {
  schema: 1;
  enabled: boolean;
  mode: 'voice' | 'video';
  /** null = built-in script + bundled audio */
  voice: Record<ExperienceLocale, WelcomeVoiceLocale> | null;
  videos: Record<ExperienceLocale, VideoAsset | null>;
}

export type Versioned<T> = T & { version: number };

export interface AppExperiencePublic {
  intro_video: Versioned<IntroVideoConfig> | null;
  abby_welcome: Versioned<AbbyWelcomeConfig> | null;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; errors: string[] };

/**
 * Why the phone shows the BUILT-IN welcome although GET /v1/me names a
 * published voice-mode version (spec §6.8; final whole-branch review minor 5).
 * Sent with WelcomeCardRendered as a LOGGED fact, never a CloudWatch dimension.
 *   config_missing    no cached copy of that version (the launch fetch has not
 *                     stored it yet, it failed, or an older one is cached)
 *   invalid_config    the cached copy of that version does not pass the contract
 *   not_ready         the audio file is not downloaded and verified yet
 *   download_failed   the last download of the file failed (network, HTTP, disk)
 *   checksum_mismatch the downloaded file failed verification (size or sha256)
 *   storage_error     the phone could not read its storage or file system
 */
export const WELCOME_FALLBACK_REASONS = [
  'config_missing',
  'invalid_config',
  'not_ready',
  'download_failed',
  'checksum_mismatch',
  'storage_error',
] as const;
export type WelcomeFallbackReason = (typeof WELCOME_FALLBACK_REASONS)[number];

// ---- Hashes -----------------------------------------------------------------
export function welcomeScriptHash(paragraphs: string[]): string {
  return sha256(JSON.stringify([paragraphs, TTS_MODEL, TTS_VOICE, TTS_INSTRUCTIONS]));
}

export function paragraphHash(text: string): string {
  return sha256(JSON.stringify([text, TTS_MODEL, TTS_VOICE, TTS_INSTRUCTIONS]));
}

// ---- Text rules (ONE set: publish, TTS, upload file name, note) -------------
//
// Admin text reaches members' screens, Abby's voice and the instructions of the
// live voice model. A character that a reviewer cannot see must not get there:
//   - C0/C1 control characters (NUL, tab, newline ...);
//   - U+2028 / U+2029 (line / paragraph separator);
//   - every Unicode format character (general category Cf): zero-width
//     characters, bidi controls, tag characters (U+E0000 block), U+FEFF ...;
//   - lone UTF-16 surrogates (Postgres refuses them: a 500, not a 400).
// No regular expression: `\p{Cf}` needs Unicode property escapes, and this
// file also runs in the app (Hermes). FORMAT_RANGES is Cf of Unicode 16.0;
// contract.test.ts compares it with Node's `\p{Cf}` for EVERY code point.

/** Why a text is refused. */
export type UnsafeTextKind = 'line_break' | 'control' | 'invisible' | 'broken';

/** Unicode general category Cf (format), inclusive ranges, Unicode 16.0. */
export const FORMAT_CHAR_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x00ad, 0x00ad],
  [0x0600, 0x0605],
  [0x061c, 0x061c],
  [0x06dd, 0x06dd],
  [0x070f, 0x070f],
  [0x0890, 0x0891],
  [0x08e2, 0x08e2],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x206f],
  [0xfeff, 0xfeff],
  [0xfff9, 0xfffb],
  [0x110bd, 0x110bd],
  [0x110cd, 0x110cd],
  [0x13430, 0x1343f],
  [0x1bca0, 0x1bca3],
  [0x1d173, 0x1d17a],
  [0xe0001, 0xe0001],
  [0xe0020, 0xe007f],
];

export function isFormatChar(codePoint: number): boolean {
  for (const [lo, hi] of FORMAT_CHAR_RANGES) {
    if (codePoint < lo) return false;
    if (codePoint <= hi) return true;
  }
  return false;
}

/**
 * The first reason `text` must be refused, or null when it is safe.
 * `allowLineBreaks` (a note, which has several lines) accepts LF and CR only;
 * every other rule stays.
 */
export function findUnsafeTextChar(text: string, opts: { allowLineBreaks?: boolean } = {}): UnsafeTextKind | null {
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const next = text.charCodeAt(i + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return 'broken';
      if (isFormatChar((c - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000)) return 'invisible';
      i++;
      continue;
    }
    if (c >= 0xdc00 && c <= 0xdfff) return 'broken';
    if (c === 0x0a || c === 0x0d) {
      if (opts.allowLineBreaks) continue;
      return 'line_break';
    }
    if (c === 0x85 || c === 0x2028 || c === 0x2029) return 'line_break';
    if (c <= 0x1f || (c >= 0x7f && c <= 0x9f)) return 'control';
    if (isFormatChar(c)) return 'invisible';
  }
  return null;
}

/** Plain words for a refused text. `subject` names it, e.g. "Paragraph 2" or "The file name". */
export function unsafeTextMessage(subject: string, kind: UnsafeTextKind): string {
  switch (kind) {
    case 'line_break':
      return `${subject} has a line break. Remove it.`;
    case 'control':
      return `${subject} has a control character (for example a tab). Remove it.`;
    case 'invisible':
      return `${subject} has an invisible character (for example a zero-width space or a direction mark). Remove it.`;
    case 'broken':
      return `${subject} has a broken character. Type the text again.`;
  }
}

// ---- Validators -------------------------------------------------------------
type Rec = Record<string, unknown>;

const isRec = (x: unknown): x is Rec => typeof x === 'object' && x !== null && !Array.isArray(x);
const isInt = (x: unknown): x is number => typeof x === 'number' && Number.isInteger(x);
const isHex64 = (x: unknown): x is string => typeof x === 'string' && /^[0-9a-f]{64}$/.test(x);
const isFiniteNonNeg = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0;
const LOCALES: ExperienceLocale[] = ['en', 'es'];
const LOCALE_NAME: Record<ExperienceLocale, string> = { en: 'English', es: 'Spanish' };

// ---- Media paths (the ONLY key shapes the server writes under m/) ----------
// The CDN serves the bucket's m/ prefix at its root, so the m/ key
// `m/intro/<sha>.mp4` is the URL `<origin>/intro/<sha>.mp4`.
export const mediaPaths = {
  introVideo: (sha256: string): string => `intro/${sha256}.mp4`,
  introPoster: (sha256: string): string => `intro/${sha256}.jpg`,
  welcomeAudio: (scriptHash: string, locale: ExperienceLocale): string => `welcome/${scriptHash}/${locale}.mp3`,
  welcomeParagraph: (paragraphHashHex: string): string => `welcome-tts/${paragraphHashHex}.mp3`,
} as const;

const trimOrigin = (origin: string): string => origin.replace(/\/+$/, '');

/**
 * An https URL with no dot segment, no backslash, no query and no fragment.
 * mediaOrigin null (the app has no media origin): that is all. With an origin,
 * the URL must also be on it.
 */
function urlOk(url: unknown, mediaOrigin: string | null): boolean {
  if (typeof url !== 'string' || !/^https:\/\/[^\s/?#\\]+\/[^\s?#\\]*$/.test(url)) return false;
  if (url.slice('https://'.length).split('/').some((seg) => seg === '.' || seg === '..')) return false;
  if (mediaOrigin === null) return true;
  return url.startsWith(`${trimOrigin(mediaOrigin)}/`);
}

/** With an origin: the URL must be EXACTLY `<origin>/<path>`. Without one: urlOk only. */
function exactMediaUrl(url: unknown, mediaOrigin: string | null, path: string): boolean {
  if (!urlOk(url, mediaOrigin)) return false;
  return mediaOrigin === null || url === `${trimOrigin(mediaOrigin)}/${path}`;
}

const POSTER_PATH = /^intro\/[0-9a-f]{64}\.jpg$/;

/**
 * Deep check of one locale's timing. The shape alone is not enough: the
 * public mapping and the phone read every entry. Each entry has
 * `index === i`; every time is a finite number >= 0; starts strictly increase;
 * each end is at or after its start; every start is before the total and no
 * end passes it.
 */
export function isValidVoiceTiming(timing: unknown, paragraphCount: number): timing is WelcomeTiming {
  if (!isRec(timing) || !Array.isArray(timing.paragraphs)) return false;
  const total = timing.totalDurationMs;
  if (!isFiniteNonNeg(total) || total <= 0) return false;
  const ps = timing.paragraphs as unknown[];
  if (ps.length !== paragraphCount || paragraphCount < 1) return false;
  let prevStart = -1;
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i];
    if (!isRec(p) || p.index !== i) return false;
    const { startMs, endMs } = p;
    if (!isFiniteNonNeg(startMs) || !isFiniteNonNeg(endMs)) return false;
    if (startMs <= prevStart || startMs >= total || endMs < startMs || endMs > total) return false;
    prevStart = startMs;
  }
  return true;
}

function checkVideo(v: unknown, label: string, origin: string | null, errors: string[]): void {
  if (!isRec(v)) {
    errors.push(`${label}: video is not valid.`);
    return;
  }
  if (!urlOk(v.url, origin)) errors.push(`${label}: video URL must be an https URL on the media host.`);
  else if (isHex64(v.sha256) && !exactMediaUrl(v.url, origin, mediaPaths.introVideo(v.sha256))) {
    errors.push(`${label}: video URL is not the converted file with this checksum. Upload the video again.`);
  }
  if (!urlOk(v.poster_url, origin)) errors.push(`${label}: poster URL must be an https URL on the media host.`);
  else if (origin !== null && !POSTER_PATH.test(String(v.poster_url).slice(trimOrigin(origin).length + 1))) {
    errors.push(`${label}: poster URL is not a converted poster. Upload the video again.`);
  }
  if (!isHex64(v.sha256)) errors.push(`${label}: video checksum is not valid.`);
  if (!isInt(v.bytes) || v.bytes <= 0 || v.bytes > VIDEO_OUTPUT_MAX_BYTES) {
    errors.push(`${label}: video size must be more than 0 and at most ${VIDEO_OUTPUT_MAX_BYTES / (1024 * 1024)} MB.`);
  }
  if (!isInt(v.duration_ms) || v.duration_ms <= 0 || v.duration_ms > VIDEO_MAX_DURATION_MS) {
    errors.push(`${label}: video must be at most ${VIDEO_MAX_DURATION_MS / 1000} seconds long.`);
  }
  if (!isInt(v.width) || !isInt(v.height) || v.width <= 0 || v.height <= 0) {
    errors.push(`${label}: video size in pixels is not valid.`);
  } else {
    const aspect = v.width / v.height;
    if (aspect < VIDEO_MIN_ASPECT || aspect > VIDEO_MAX_ASPECT) {
      errors.push(`${label}: video shape must be between 2:1 and 1:2 (width to height).`);
    }
  }
  if (v.description !== null && v.description !== undefined) {
    if (typeof v.description !== 'string' || v.description.length > DESCRIPTION_MAX_CHARS) {
      errors.push(`${label}: description must be at most ${DESCRIPTION_MAX_CHARS} characters.`);
    } else {
      // The shared text rule: a NUL or a lone surrogate would make Postgres
      // refuse the JSONB insert (a 500). Line breaks are allowed (a note).
      const kind = findUnsafeTextChar(v.description, { allowLineBreaks: true });
      if (kind !== null) errors.push(`${label}: ${unsafeTextMessage('The description', kind)}`);
    }
  }
}

function checkVideos(
  videos: unknown,
  origin: string | null,
  errors: string[],
  englishRequired: boolean,
): void {
  if (!isRec(videos)) {
    errors.push('Videos are missing.');
    return;
  }
  for (const loc of LOCALES) {
    const v = videos[loc];
    if (v === null || v === undefined) {
      if (loc === 'en' && englishRequired) errors.push('English video is required.');
    } else {
      checkVideo(v, `${LOCALE_NAME[loc]} video`, origin, errors);
    }
  }
}

function checkVoiceLocale(v: unknown, loc: ExperienceLocale, origin: string | null, errors: string[]): void {
  const label = `${LOCALE_NAME[loc]} voice`;
  if (!isRec(v)) {
    errors.push(`${label}: is missing.`);
    return;
  }
  const ps = v.paragraphs;
  let textOk = false;
  if (!Array.isArray(ps) || ps.length < WELCOME_MIN_PARAGRAPHS || ps.length > WELCOME_MAX_PARAGRAPHS) {
    errors.push(`${label}: use ${WELCOME_MIN_PARAGRAPHS} to ${WELCOME_MAX_PARAGRAPHS} paragraphs.`);
  } else if (ps.some((p) => typeof p !== 'string' || p.trim() === '')) {
    errors.push(`${label}: each paragraph must have text.`);
  } else if (ps.some((p: string) => p.length > WELCOME_PARAGRAPH_MAX_CHARS)) {
    errors.push(`${label}: each paragraph must be at most ${WELCOME_PARAGRAPH_MAX_CHARS} characters.`);
  } else {
    const bad = (ps as string[]).map((p) => findUnsafeTextChar(p)).findIndex((k) => k !== null);
    if (bad >= 0) {
      errors.push(`${label}: ${unsafeTextMessage(`paragraph ${bad + 1}`, findUnsafeTextChar((ps as string[])[bad]!)!)}`);
    } else {
      textOk = true;
    }
  }
  const hashOk = textOk && v.script_hash === welcomeScriptHash(ps as string[]);
  if (textOk && !hashOk) {
    errors.push(`${label}: audio does not match the text. Generate the audio again.`);
  }
  if (!urlOk(v.audio_url, origin)) errors.push(`${label}: audio URL must be an https URL on the media host.`);
  else if (hashOk && !exactMediaUrl(v.audio_url, origin, mediaPaths.welcomeAudio(v.script_hash as string, loc))) {
    errors.push(`${label}: audio URL is not the voice file for this text. Generate the audio again.`);
  }
  if (!isHex64(v.audio_sha256)) errors.push(`${label}: audio checksum is not valid.`);
  const t = v.timing;
  if (!isRec(t) || !Array.isArray(t.paragraphs)) {
    errors.push(`${label}: timing is not valid.`);
  } else if (Array.isArray(ps) && t.paragraphs.length !== ps.length) {
    errors.push(`${label}: timing must have one entry for each paragraph.`);
  } else if (Array.isArray(ps) && !isValidVoiceTiming(t, ps.length)) {
    errors.push(`${label}: timing is not valid. Generate the audio again.`);
  }
}

function envelope(x: unknown, label: string, errors: string[]): x is Rec {
  if (!isRec(x)) {
    errors.push(`${label} is not valid.`);
    return false;
  }
  if (x.schema !== 1) errors.push(`${label}: schema must be 1.`);
  if (typeof x.enabled !== 'boolean') errors.push(`${label}: enabled must be true or false.`);
  return true;
}

export function validateIntroVideoConfig(x: unknown, mediaOrigin: string | null): ValidationResult<IntroVideoConfig> {
  const errors: string[] = [];
  if (envelope(x, 'Intro video', errors)) {
    if (!isInt(x.audience_pct) || x.audience_pct < 0 || x.audience_pct > 100) {
      errors.push('Audience must be a whole number from 0 to 100.');
    }
    checkVideos(x.videos, mediaOrigin, errors, x.enabled === true);
  }
  return errors.length ? { ok: false, errors } : { ok: true, value: x as unknown as IntroVideoConfig };
}

export function validateAbbyWelcomeConfig(x: unknown, mediaOrigin: string | null): ValidationResult<AbbyWelcomeConfig> {
  const errors: string[] = [];
  if (envelope(x, 'Abby welcome', errors)) {
    if (x.mode !== 'voice' && x.mode !== 'video') errors.push('Mode must be voice or video.');
    if (x.voice !== null) {
      if (!isRec(x.voice)) errors.push('Voice is not valid.');
      else for (const loc of LOCALES) checkVoiceLocale(x.voice[loc], loc, mediaOrigin, errors);
    }
    checkVideos(x.videos, mediaOrigin, errors, x.mode === 'video');
  }
  return errors.length ? { ok: false, errors } : { ok: true, value: x as unknown as AbbyWelcomeConfig };
}

/** Never throws. An invalid surface becomes null. mediaOrigin null = https check only. */
export function parseAppExperiencePublic(x: unknown, mediaOrigin: string | null): AppExperiencePublic {
  const out: AppExperiencePublic = { intro_video: null, abby_welcome: null };
  if (!isRec(x)) return out;
  try {
    const iv = x.intro_video;
    if (isRec(iv) && isInt(iv.version)) {
      const r = validateIntroVideoConfig(iv, mediaOrigin);
      if (r.ok) out.intro_video = { ...r.value, version: iv.version };
    }
    const aw = x.abby_welcome;
    if (isRec(aw) && isInt(aw.version)) {
      const r = validateAbbyWelcomeConfig(aw, mediaOrigin);
      if (r.ok) out.abby_welcome = { ...r.value, version: aw.version };
    }
  } catch {
    // Defensive: a hostile payload must never crash the caller.
  }
  return out;
}
